// externals
import type { FC } from 'react';
import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Typography } from '@interstate/components/Typography';
import { CheckCircleIcon, ArrowTrendingUpIcon, ShieldSolidIcon } from '@interstate/components/Icons';
import { Box } from '@interstate/components/Box';
import { Badge } from '@interstate/components/Badge';
import { RadioButtonList } from '@interstate/components/RadioButtonList';
import type { BadgeVariant } from '@interstate/components/Badge';

// internal libraries
import {
    formatDollarsAndCents,
    formatDollars,
    formatAPR,
    formatNumber,
    getNumberOfDaysStartingFromTargetDate
} from '@makemydeal/dr-common-utils';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerInfoSelectors from '@makemydeal/dr-dash-store/selectors/offerInfoSelectors';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as deskingSelectors from '@makemydeal/dr-dash-store/selectors/deskingSelectors';
import {
    vehicleRecommendationDisplayed,
    vehicleRecommendationSelected,
    softpullAltConsentContinueClicked,
    softpullMandatoryDetailsContinueClicked
} from '@makemydeal/dr-dash-store/actions/deskingActionCreators';
import * as customerSpdSelectors from '@makemydeal/dr-dash-store/selectors/customerSpdSelectors';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import {
    AltSoftPullConsentAlert,
    MandatoryDetailsCard,
    useMandatoryDetailsSubmit,
    dealLockHooks
} from '@makemydeal/dr-dash-components';
import { useOnceInView } from '../hooks/useOnceInView';

// assets
import VehiclePlaceholderSvg from '../../../../assets/blankVehicle.svg';

// styles
import { AiAffordabilitySlideoutContainer, CreditSourceBannerContainer } from './AiVehicleRecommendationSlideout.style';
import { AiLogoIcon } from '@makemydeal/dr-shared-components';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';

const formatVehicleAge = (inventoryDate?: string): string => {
    const days = inventoryDate ? getNumberOfDaysStartingFromTargetDate(inventoryDate) : undefined;
    if (days === undefined) return 'N/A';
    return `${days} day${days === 1 ? '' : 's'}`;
};

/**
 * Returns the Badge variant based on affordability level
 */
const getAffordabilityStatus = (level: string): BadgeVariant => {
    const affordabilityLevel = level.toUpperCase();
    return affordabilityLevel === 'HIGH' ? 'success' : 'light';
};

/**
 * Normalizes the affordability level text (e.g., "HIGH" -> "High")
 */
const normalizeAffordabilityLevel = (level: string): string => {
    return level.charAt(0).toUpperCase() + level.slice(1).toLowerCase();
};

const prefix = 'ai-vehicle-recommendation-slideout';

const PRIMARY_VEHICLE = '-1';

/**
 * Slideout content component for AI Vehicle Recommendation details
 */
export const AiVehicleRecommendationSlideout: FC<{
    vehicles: any[];
    scopeId: string;
    onClose?: () => void;
    selectedVehicleIndex: string;
    setSelectedVehicleIndex: (index: string) => void;
}> = ({ vehicles, onClose, selectedVehicleIndex, setSelectedVehicleIndex }) => {
    const dispatch = useDispatch();
    const isLocked = dealLockHooks.useIsDealLocked();
    const theme = useInterstateTheme();
    // Get current vehicle and payment details from store
    const currentVehicle = useSelector(vehicleSelectors.getVehicle);
    const monthlyPayment = useSelector(offerSelectors.getMonthlyPayment);
    const sellingPrice = useSelector(offerSelectors.getSellingPrice);
    const downPayment = useSelector(offerSelectors.getDownPayment);
    const apr = useSelector(offerReduxSelectors.getApr);

    // Soft pull consent state (DL or First Name, Last Name, Zip, DOB when toggle enabled)
    const softPullConsentProvided = useSelector(offerInfoSelectors.getSoftPullConsentProvided);
    const isSoftPull = useSelector(deskingSelectors.getIsSoftPull);
    const hasIdentityDataCompleteness = useSelector(customerSpdSelectors.getHasIdentityDataCompleteness);
    const isEnableSoftPullConsentMV = useSelector(featureToggleSelectors.isEnableSoftPullConsentMV);
    const isEnableSoftPullConsentWithoutDLMV = useSelector(featureToggleSelectors.isEnableSoftPullConsentWithoutDLMV);
    const isSpdManagementEnabled = useSelector(dealerSelectors.isSpdManagementEnabled);
    const vehicleAnalyticsPayload = useSelector(vehicleSelectors.getVehicleAnalyticsPayload);
    const creditDecisionLender = useSelector(offerReduxSelectors.getCreditDecisionLender);
    const lenderId = creditDecisionLender?.id;
    const lenderName = creditDecisionLender?.name;

    // Fire analytics for each vehicle recommendation when displayed (dc-mv-261)
    const inViewRef = useOnceInView(
        useCallback(() => {
            if (vehicles.length > 0) {
                vehicles.forEach((recommendation) => {
                    const { year, make, model, vin, cadsVehicleCatalogId } = recommendation.vehicle;
                    const { level } = recommendation.affordability;

                    dispatch(
                        vehicleRecommendationDisplayed({
                            make,
                            model,
                            year,
                            vin,
                            cadsVehicleCatalogId,
                            acceptanceLikelihood: level.toLowerCase()
                        })
                    );
                });
            }
        }, [])
    );

    const shouldShowConsentAlert = isEnableSoftPullConsentMV && softPullConsentProvided === false && hasIdentityDataCompleteness;
    // State 1 (Mandatory Details) is the "soft-pull without DL" path — gate on the no-DL toggle so
    // we preserve the existing master-switch semantic: toggle OFF + no DL = no soft-pull UI at all.
    // softPullConsentProvided check uses !== true (not === false) because the modal that flips it
    // to false only opens when identity is complete; State 1 fires on the inverse, so the value
    // stays undefined on fresh deals.
    const shouldShowMandatoryDetails =
        isEnableSoftPullConsentMV &&
        softPullConsentProvided !== true &&
        !hasIdentityDataCompleteness &&
        isSpdManagementEnabled &&
        isEnableSoftPullConsentWithoutDLMV;

    const handleConsentAccept = () => {
        dispatch(softpullAltConsentContinueClicked(vehicleAnalyticsPayload)); // dc-mv-281: Analytics tag
        dispatch(offerActionCreators.setSoftPullConsentProvided(true));
        onClose?.();
    };

    const {
        initialValues: mandatoryDetailsInitialValues,
        onSubmit: handleMandatoryDetailsSubmit,
        isSubmitting: isMandatoryDetailsSubmitting
    } = useMandatoryDetailsSubmit({
        onComplete: () => onClose?.()
    });

    return (
        <AiAffordabilitySlideoutContainer ref={inViewRef} data-testid={`${prefix}-container`}>
            <Typography variant="body-md" sx={{ marginBottom: '20px', color: '#5A5A5A' }}>
                Explore more affordable vehicles in our inventory with better payment options. You can create up to two additional
                scenarios.
            </Typography>

            {isSoftPull && (
                <CreditSourceBannerContainer data-testid={`${prefix}-credit-source-banner`} interstateTheme={theme}>
                    <Box className="credit-source-banner-icon-wrapper">
                        <ShieldSolidIcon color={theme.tokens.SemColorSurfaceButtonPrimaryRest} width="16" height="16" />
                        <Typography
                            variant="label-sm"
                            sx={{ textTransform: 'uppercase', color: theme.tokens.SemColorOnSurfaceMuted, fontWeight: 600 }}
                        >
                            Credit Source
                        </Typography>
                    </Box>
                    <Typography variant="h5" sx={{ color: '#0F172A' }}>
                        Soft Pull
                    </Typography>
                </CreditSourceBannerContainer>
            )}
            {shouldShowMandatoryDetails && (
                <MandatoryDetailsCard
                    initialValues={mandatoryDetailsInitialValues}
                    onSubmit={(values) => {
                        dispatch(softpullMandatoryDetailsContinueClicked(vehicleAnalyticsPayload)); // dc-mv-300: Analytics tag
                        handleMandatoryDetailsSubmit(values);
                    }}
                    isSubmitting={isMandatoryDetailsSubmitting}
                />
            )}
            {shouldShowConsentAlert && <AltSoftPullConsentAlert onAccept={handleConsentAccept} />}
            <Box className={`${prefix}-cards-container`}>
                {/* Primary Vehicle Card */}
                <Box className={`${prefix}-card primary`} onClick={() => !isLocked && setSelectedVehicleIndex(PRIMARY_VEHICLE)}>
                    <Box className={`${prefix}-card-header`}>
                        <RadioButtonList
                            name="vehicle-selection"
                            displayLabel={false}
                            options={[
                                {
                                    label: selectedVehicleIndex === PRIMARY_VEHICLE ? 'Selected' : 'Select',
                                    value: PRIMARY_VEHICLE,
                                    disabled: isLocked,
                                    checkedDisabled: isLocked
                                }
                            ]}
                            value={selectedVehicleIndex}
                            onChange={(value) => setSelectedVehicleIndex(value.target.value as string)}
                        />
                        <Badge data-testid={`${prefix}-badge`} variant={'info'}>
                            Primary
                        </Badge>
                    </Box>
                    <Box className={`${prefix}-vehicle-info`}>
                        <img src={currentVehicle.imageUrl || VehiclePlaceholderSvg} alt="Vehicle Image" />
                        <Box className={`${prefix}-vehicle-details`}>
                            <Typography variant="h5">
                                {currentVehicle.year} {currentVehicle.make} {currentVehicle.model} {currentVehicle.trim}
                            </Typography>
                            <Typography variant="body-sm">
                                {currentVehicle.exteriorColor} | {formatNumber(currentVehicle.mileage)} miles
                            </Typography>
                        </Box>
                    </Box>
                    <Box className={`${prefix}-vehicle-payment-details`}>
                        <Box className={`${prefix}-detail-item`}>
                            <Typography variant="label-sm">Monthly Payment</Typography>
                            <Typography variant="body-sm">{formatDollars(monthlyPayment)}</Typography>
                        </Box>
                        <Box className={`${prefix}-detail-item`}>
                            <Typography variant="label-sm">Selling Price</Typography>
                            <Typography variant="body-sm">{formatDollarsAndCents(sellingPrice)}</Typography>
                        </Box>
                        <Box className={`${prefix}-detail-item`}>
                            <Typography variant="label-sm">Down Payment</Typography>
                            <Typography variant="body-sm">{formatDollarsAndCents(downPayment)}</Typography>
                        </Box>
                        <Box className={`${prefix}-detail-item`}>
                            <Typography variant="label-sm">APR</Typography>
                            <Typography variant="body-sm">{formatAPR(apr)}%</Typography>
                        </Box>
                        <Box className={`${prefix}-detail-item`}>
                            <Typography variant="label-sm">Age</Typography>
                            <Typography variant="body-sm">{formatVehicleAge(currentVehicle.inventoryDate)}</Typography>
                        </Box>
                    </Box>
                </Box>
                <div className={`${prefix}-cards-divider`} />
                <Typography
                    variant="h3"
                    sx={{
                        gap: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        position: 'sticky',
                        top: 0,
                        backgroundColor: '#fff',
                        zIndex: 1,
                        paddingTop: '16px',
                        marginTop: '-16px'
                    }}
                >
                    AI Recommended Vehicles
                    <AiLogoIcon data-testid={`${prefix}-logo`} fontSize="24px" ariaHidden={true} />
                </Typography>
                {/* Vehicle Recommendations Cards */}
                {vehicles.length > 0 &&
                    vehicles.map((recommendation, index) => {
                        const {
                            year,
                            make,
                            model,
                            trim,
                            exteriorColor,
                            odometer,
                            imageUrl,
                            listedPrice,
                            vin,
                            cadsVehicleCatalogId,
                            inventoryDate
                        } = recommendation.vehicle;
                        const { monthlyPayment, downPayment, apr, vehicleProfit } = recommendation.payment;
                        const { level } = recommendation.affordability;

                        return (
                            <Box
                                className={`${prefix}-card`}
                                key={index}
                                onClick={() => {
                                    if (!isLocked) {
                                        setSelectedVehicleIndex(`${index}`);
                                        dispatch(
                                            vehicleRecommendationSelected({
                                                vin,
                                                make,
                                                model,
                                                year,
                                                cadsVehicleCatalogId,
                                                loanLikelihood: level.toLowerCase(),
                                                lenderId,
                                                lenderName
                                            })
                                        );
                                    }
                                }}
                            >
                                <Box className={`${prefix}-card-header`}>
                                    <RadioButtonList
                                        name="vehicle-selection"
                                        displayLabel={false}
                                        options={[
                                            {
                                                label: selectedVehicleIndex === `${index}` ? 'Selected' : 'Select',
                                                value: `${index}`,
                                                disabled: isLocked,
                                                checkedDisabled: isLocked
                                            }
                                        ]}
                                        value={selectedVehicleIndex}
                                        onChange={(value) => setSelectedVehicleIndex(value.target.value as string)}
                                    />
                                </Box>
                                <Box className={`${prefix}-vehicle-info`}>
                                    <img src={imageUrl || VehiclePlaceholderSvg} alt="Vehicle Image" />
                                    <Box className={`${prefix}-vehicle-details`}>
                                        <Typography variant="h5">
                                            {year} {make} {model} {trim}
                                        </Typography>
                                        <Typography variant="body-sm" sx={{ color: '#5A5A5A' }}>
                                            {exteriorColor} | {formatNumber(odometer)} miles
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box className={`${prefix}-vehicle-payment-details`}>
                                    <Box className={`${prefix}-detail-item`}>
                                        <Typography variant="label-sm">Monthly Payment</Typography>
                                        <Typography variant="body-sm">{formatDollars(monthlyPayment)}</Typography>
                                    </Box>
                                    <Box className={`${prefix}-detail-item`}>
                                        <Typography variant="label-sm">Selling Price</Typography>
                                        <Typography variant="body-sm">{formatDollarsAndCents(listedPrice)}</Typography>
                                    </Box>
                                    <Box className={`${prefix}-detail-item`}>
                                        <Typography variant="label-sm">Down Payment</Typography>
                                        <Typography variant="body-sm">{formatDollarsAndCents(downPayment)}</Typography>
                                    </Box>
                                    <Box className={`${prefix}-detail-item`}>
                                        <Typography variant="label-sm">APR</Typography>
                                        <Typography variant="body-sm">{formatAPR(apr)}%</Typography>
                                    </Box>
                                    <Box className={`${prefix}-detail-item`}>
                                        <Typography variant="label-sm">Age</Typography>
                                        <Typography variant="body-sm">{formatVehicleAge(inventoryDate)}</Typography>
                                    </Box>
                                    <Box className={`${prefix}-profit-approval-wrapper`}>
                                        <Box className={`${prefix}-profit-approval-container`}>
                                            <Box className={`${prefix}-detail-item`}>
                                                <Box className={`${prefix}-detail-item-label`}>
                                                    <ArrowTrendingUpIcon color="#53AC39" />
                                                    <Typography variant="h6">Profit Potential</Typography>
                                                </Box>
                                                <Typography variant="h5" color="#3C7B29">
                                                    +{formatDollarsAndCents(vehicleProfit)}
                                                </Typography>
                                            </Box>
                                            <Box className={`${prefix}-detail-item`}>
                                                <Box className={`${prefix}-detail-item-label`}>
                                                    <CheckCircleIcon color="#003468" />
                                                    <Typography variant="h6">Acceptance Likelihood</Typography>
                                                </Box>
                                                <Badge variant={getAffordabilityStatus(level)}>
                                                    {normalizeAffordabilityLevel(level)}
                                                </Badge>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
            </Box>
        </AiAffordabilitySlideoutContainer>
    );
};
